import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useMessage } from '@tmapy/intl';

import {
  DangerAlert,
  WaitingAlert,
  FormRow,
  FormItem,
  BtnBar,
  CtaBtn,
  InfoBadge,
  SuccessAlert,
  Input,
  Form,
} from '@tmapy/style-guide';

import { useAuth, useFormItemServerErrors, useInput, useSelector } from '../../hooks';
import type { InputProps } from '../../hooks/useInput';

import { MainContent } from '../../app/components/MainContent';
import { PageTitle } from '../../app/components/PageTitle';
import { QUERY_STATE } from '../../constants';

import type { PatchedGeodesistUpdateRequest } from '../../openapi/types';
import { geodesistsPartialUpdate } from '../../openapi/operations/geodesistsPartialUpdate';
import {
  GeodesistsRetrieveResponse,
  useGeodesistsRetrieveResource,
} from '../../openapi/operations/geodesistsRetrieve';

import { GEODESIST_IS_ACTIVE } from '../constants';
import {
  geodesistBtnMessages,
  geodesistMessages,
  geodesistQueryMessages,
  isActiveMessages,
} from './geodesistMessages';
import { useCertification } from './geodesistsHooks';

type DetailProps = {
  geodesistId: string;
};

export const GeodesistDetail: React.FC<DetailProps> = React.memo((props) => {
  const auth = useAuth();
  const [saveQueryState, setSaveQueryState] = useState<QUERY_STATE>(QUERY_STATE.EMPTY);
  const serverErrors = useSelector((state) => state.docedit.errors);
  const formItemServerErrors = useFormItemServerErrors(serverErrors);

  const geodesistId = parseInt(props.geodesistId, 10);

  const resource = useGeodesistsRetrieveResource(geodesistId ? { id: geodesistId } : null);
  const data = useMemo(() => {
    return (resource.data ? { ...resource.data } : {}) as GeodesistsRetrieveResponse;
  }, [resource.data]);

  const formatMessage = useMessage();

  const certifications = useCertification(
    data.has_certification_a,
    data.has_certification_b,
    data.has_certification_c,
  );

  // ACL
  const acl = useMemo(
    () => ({
      /**
       * update a partial_update je nutno mit nastaveno stejne (FE,BE), ukladani vzdy jde pres PATCH tedy PartialUpdate
       * partial_update urcuje form disable jednotlivych polozek
       * update urcuje viditelnost ukladaciho tlacitka
       */
      canUpdate: auth.hasPermission('Geodesists', 'partial_update', data),
      geodesistsUpdate: auth.hasPermission('Geodesists', 'update', data),
    }),
    [auth, data],
  );

  // HOOK FOR INPUTS
  const formItemChangeCallback = useCallback(() => {
    setSaveQueryState(QUERY_STATE.EMPTY);
  }, [setSaveQueryState]);

  // NOTES
  const note = useInput<InputProps>({
    name: 'note',
    isDisabled: !acl.canUpdate,
    initialValue: data.note,
    label: formatMessage(geodesistMessages.note),
    onChange: formItemChangeCallback,
    ...formItemServerErrors.note,
  });

  // ACTIONS
  const handleSave = useCallback(async () => {
    const values: PatchedGeodesistUpdateRequest = {
      note: note.value || '',
    };

    setSaveQueryState(QUERY_STATE.WAITING);

    try {
      const response = await geodesistsPartialUpdate({
        query: { id: geodesistId },
        request: values,
      });

      if (response.ok) {
        setSaveQueryState(QUERY_STATE.SUCCESS);
      } else {
        setSaveQueryState(QUERY_STATE.FAIL);
      }
    } catch (e) {
      setSaveQueryState(QUERY_STATE.FAIL);
    }
    return null;
  }, [note]);

  return (
    <MainContent width='full'>
      <article className='sg-u-vs-4'>
        <div className='dmx-doc--head sg-u-vs-2'>
          {resource.queryState === QUERY_STATE.SUCCESS && (
            <div className='sg-a-d-f sg-a-g-2 sg-a-ai-b sg-a-jc-sb sg-a-fw-w'>
              <header className='sg-a-d-f sg-a-ai-c sg-u-hsl-1'>
                <PageTitle>
                  <FormattedMessage
                    {...geodesistMessages.geodesistDetailTitle}
                    values={{ id: data.official_number || 'empty' }}
                  />
                </PageTitle>
              </header>
              <BtnBar>
                {acl.geodesistsUpdate && (
                  <CtaBtn onClick={handleSave}>
                    <FormattedMessage {...geodesistBtnMessages.save} />
                  </CtaBtn>
                )}
              </BtnBar>
            </div>
          )}

          {(saveQueryState === QUERY_STATE.WAITING ||
            resource.queryState === QUERY_STATE.WAITING) && (
            <WaitingAlert>
              <FormattedMessage {...geodesistQueryMessages.submitQueryStateWaiting} />
            </WaitingAlert>
          )}

          {resource.queryState === QUERY_STATE.FAIL && (
            <DangerAlert>
              <FormattedMessage {...geodesistQueryMessages.loadQueryStateFail} />
            </DangerAlert>
          )}

          {saveQueryState === QUERY_STATE.FAIL && (
            <DangerAlert>
              <FormattedMessage {...geodesistQueryMessages.submitQueryStateFail} />
            </DangerAlert>
          )}

          {saveQueryState === QUERY_STATE.SUCCESS && (
            <SuccessAlert>
              <FormattedMessage {...geodesistQueryMessages.submitQueryStateSuccess} />
            </SuccessAlert>
          )}
        </div>
        {resource.queryState === QUERY_STATE.SUCCESS && (
          <div className='sg-u-vs-4'>
            <Form onSubmit={handleSave}>
              <div className='sg-u-box sg-a-p-2 sg-a-d-f sg-a-fw-w sg-a-g-2'>
                <div className='sg-a-f-a sg-u-vs-1'>
                  <FormRow>
                    <FormItem label={formatMessage(geodesistMessages.name)}>
                      {data.name} {data.surname}
                    </FormItem>
                  </FormRow>
                  <FormRow>
                    <FormItem label={formatMessage(geodesistMessages.isActive)}>
                      <FormattedMessage
                        {...isActiveMessages[
                          data.is_active ? GEODESIST_IS_ACTIVE.TRUE : GEODESIST_IS_ACTIVE.FALSE
                        ]}
                      />
                    </FormItem>
                  </FormRow>
                  <FormRow>
                    <FormItem label={formatMessage(geodesistMessages.hasCertification)}>
                      {certifications.map((item, idx) => (
                        <span key={idx}>
                          <InfoBadge>{item}</InfoBadge>
                        </span>
                      ))}
                    </FormItem>
                  </FormRow>
                  <FormRow>
                    <FormItem {...note}>
                      <Input {...note} />
                    </FormItem>
                  </FormRow>
                </div>
              </div>
            </Form>
          </div>
        )}
      </article>
    </MainContent>
  );
});
